import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Search, Upload, Trash2, Loader2, BookOpen, X, ImageOff, Plus, ZoomIn, Info, FileText, Layers, Table, Image as ImageIcon } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useNavigate, useSearchParams } from "react-router-dom";

const PENDING_KEY = "arv_pending_parts";

// Drawings & covers require Authorization, so we fetch them as blobs (axios injects the token).
function normalize(u) {
  if (!u) return "";
  if (/^https?:\/\//.test(u)) return u;
  return u.replace(/^\/?api\//, "").replace(/^\/+/, "");
}

function AuthImage({ path, alt, className, fit = "cover" }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true; let objUrl;
    setSrc(null); setFailed(false);
    if (!path) { setFailed(true); return; }
    api.get(normalize(path), { responseType: "blob" })
      .then((r) => { if (!active) return; objUrl = URL.createObjectURL(r.data); setSrc(objUrl); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  if (failed) return <div className={`${className} bg-[#F5F5F5] flex items-center justify-center`}><ImageOff size={22} className="text-zinc-400" /></div>;
  if (!src) return <div className={`${className} bg-[#F5F5F5] animate-pulse`} />;
  return <img src={src} alt={alt} className={className} style={{ objectFit: fit }} />;
}

export default function SpareBook() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const isManager = ["manager", "office", "super_admin"].includes(user?.role);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState(null);
  const [status, setStatus] = useState(null);
  const [assemblies, setAssemblies] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selPos, setSelPos] = useState(null);
  const [zoom, setZoom] = useState(false);

  const loadStatus = useCallback(async (tid) => { const st = await api.get("/sparebook/status", { params: tid ? { machine_type_id: tid } : {} }); setStatus(st.data); return st.data; }, []);
  const loadAssemblies = useCallback(async (tid) => { const as = await api.get("/sparebook/assemblies", { params: tid ? { machine_type_id: tid } : {} }); setAssemblies(as.data); }, []);

  useEffect(() => {
    api.get("/machine-types").then(({ data }) => {
      setTypes(data);
      const qp = searchParams.get("type");
      const initial = (qp && data.find((x) => x.id === qp)) ? qp : (data.find((x) => x.is_default)?.id || data[0]?.id || null);
      setTypeId(initial);
    }).catch(() => setTypes([]));
  }, [searchParams]);

  useEffect(() => { if (typeId) { loadStatus(typeId); loadAssemblies(typeId); } }, [typeId, loadStatus, loadAssemblies]);

  // Poll while the uploaded PDF is still processing.
  useEffect(() => {
    if (status?.status !== "processing" || !typeId) return;
    const iv = setInterval(async () => { const s = await loadStatus(typeId); if (s.status !== "processing") { clearInterval(iv); loadAssemblies(typeId); } }, 3000);
    return () => clearInterval(iv);
  }, [status?.status, typeId, loadStatus, loadAssemblies]);

  useEffect(() => {
    const h = setTimeout(async () => {
      if (!typeId) return;
      if (!q.trim()) { loadAssemblies(typeId); return; }
      try { const { data } = await api.get("/sparebook/search", { params: { q, machine_type_id: typeId } }); setAssemblies(data.assemblies || data); } catch {}
    }, 300);
    return () => clearTimeout(h);
  }, [q, typeId, loadAssemblies]);

  const openAssembly = async (a) => {
    try { const { data } = await api.get(`/sparebook/assemblies/${a.id}`); setDetail(data); setSelPos(null); setZoom(false); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/sparebook/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, params: typeId ? { machine_type_id: typeId } : {}, timeout: 180000 }); toast.success(t("Parts book uploaded")); loadStatus(typeId); loadAssemblies(typeId); }
    catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const remove = async () => { if (!window.confirm(t("Delete the current spare parts book?") || "Delete the current spare parts book?")) return; try { await api.delete("/sparebook", { params: typeId ? { machine_type_id: typeId } : {} }); toast.success(t("Deleted") || "Deleted"); loadStatus(typeId); loadAssemblies(typeId); } catch (e) { toast.error(apiError(e)); } };

  const addToReport = (part) => {
    const list = JSON.parse(localStorage.getItem(PENDING_KEY) || "[]");
    if (list.some((x) => x.part_number === part.part_number)) { toast(t("Already in report") || "Already in report"); return; }
    list.push({ part_number: part.part_number, part_name: part.description || part.part_number });
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
    toast.success(t("Added to report"));
  };

  return (
    <>
      <PageHeader title={t("Parts Book")} subtitle={t("Browse assemblies, drawings & spare parts")} />
      <div className="px-4 pt-4">
        {types.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-4 text-center" data-testid="no-types">
            <p className="text-zinc-300">{isManager ? t("Create a machine type first to upload a parts book.") : t("No machine types yet.")}</p>
            {isManager && <button onClick={() => nav("/machine-types")} className="mt-3 bg-[#F26722] text-white font-bold px-4 py-2 rounded-lg" data-testid="go-machine-types">{t("Machine Types")}</button>}
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3" data-testid="type-tabs">
            {types.map((mt) => (
              <button key={mt.id} onClick={() => { setTypeId(mt.id); setQ(""); }} data-testid={`type-tab-${mt.id}`}
                className={`px-3.5 py-1.5 text-sm font-bold rounded-lg whitespace-nowrap transition-colors ${typeId === mt.id ? "bg-[#F26722] text-white" : "bg-[#18181B] border border-zinc-800 text-zinc-400 hover:text-white"}`}>
                {mt.name}{mt.is_default ? ` · ${t("Default")}` : ""}
              </button>
            ))}
          </div>
        )}
        {typeId && isManager && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mb-4">
            <p className="text-sm text-zinc-300 mb-3">
              {status?.status === "ready" ? <>{t("Current book:")} <span className="text-white font-semibold">{status.filename}</span> · {status.assemblies} {t("assemblies")} · {status.parts} {t("parts")}</>
                : status?.status === "processing" ? <span className="flex items-center gap-2 text-[#F26722]"><Loader2 className="animate-spin" size={16} /> {t("Processing the uploaded PDF…")}</span>
                : t("No spare parts book uploaded yet.")}
            </p>
            <div className="bg-[#F26722]/10 border border-[#F26722]/30 rounded-xl p-3.5 mb-3" data-testid="pdf-format-banner">
              <div className="flex items-center gap-2 text-[#F26722] font-bold text-sm mb-2.5">
                <Info size={16} /> {t("Accepted PDF format")}
              </div>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2">
                  <FileText size={15} className="text-[#F26722] shrink-0 mt-0.5" />
                  <span>{t("One")} <b className="text-white font-semibold">{t("PDF")}</b> {t("file (up to 40 MB).")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Layers size={15} className="text-[#F26722] shrink-0 mt-0.5" />
                  <span>{t("Group parts by")} <b className="text-white font-semibold">{t("assembly")}</b>{t(", each on its own page(s).")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Table size={15} className="text-[#F26722] shrink-0 mt-0.5" />
                  <span>{t("Give each assembly a parts table with")} <b className="text-white font-semibold">{t("Position no.")}</b> · <b className="text-white font-semibold">{t("Part number")}</b> · <b className="text-white font-semibold">{t("Description")}</b> {t("(price optional).")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <ImageIcon size={15} className="text-[#F26722] shrink-0 mt-0.5" />
                  <span>{t("An")} <b className="text-white font-semibold">{t("exploded-view drawing")}</b> {t("page per assembly is optional — numbered callouts on it enable tap-to-select on the image.")}</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="bg-[#F26722] hover:bg-[#D95513] text-white text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5 cursor-pointer" data-testid="upload-sparebook-btn">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {t("Upload PDF")}
                <input type="file" accept="application/pdf" className="hidden" onChange={upload} disabled={uploading} />
              </label>
              {status?.status === "ready" && (
                <button onClick={remove} data-testid="delete-sparebook-btn" className="bg-[#18181B] border border-zinc-800 text-red-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Trash2 size={16} /> {t("Delete")}</button>
              )}
            </div>
          </div>
        )}

        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="parts-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Part number, description or assembly")}
            className="w-full bg-[#141416] border border-zinc-800 text-white rounded-xl pl-11 pr-4 py-3 placeholder-zinc-500 focus:border-[#F26722] focus:outline-none" />
        </div>

        {assemblies.length === 0 && <p className="text-zinc-500 text-center py-10" data-testid="parts-empty">{t("No assemblies to show.")}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3.5 mt-4">
          {assemblies.map((a) => (
            <div key={a.id} data-testid={`assembly-card-${a.id}`} onClick={() => openAssembly(a)} className="bg-[#18181B] border border-zinc-800/90 rounded-2xl overflow-hidden flex flex-col hover:border-[#F26722]/50 transition-all cursor-pointer">
              <div className="bg-white h-28 flex items-center justify-center overflow-hidden">
                {a.cover_url ? <AuthImage path={a.cover_url} alt="" className="w-full h-full" fit="cover" /> : <BookOpen size={26} className="text-zinc-300" />}
              </div>
              <div className="p-3">
                <h3 className="text-sm font-bold text-white leading-tight line-clamp-2">{a.name}</h3>
                <p className="text-xs text-zinc-500 mt-1">{a.assembly_no || ""}{a.part_count != null ? ` · ${a.part_count} ${t("parts")}` : ""}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {detail && (
        <AssemblyModal detail={detail} selPos={selPos} setSelPos={setSelPos} zoom={zoom} setZoom={setZoom} onClose={() => setDetail(null)} onAdd={addToReport} />
      )}
    </>
  );
}

function AssemblyModal({ detail, selPos, setSelPos, zoom, setZoom, onClose, onAdd }) {
  const { t } = useLang();
  const drawing = (detail.drawings || [])[0] || null;
  const drawingPath = drawing ? (drawing.url || `sparebook/drawings/${drawing.id}.png`) : null;
  const hotspots = drawing?.hotspots || [];
  const parts = detail.parts || [];
  const selected = parts.find((p) => p.pos === selPos) || null;

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-3 md:p-4" onClick={onClose} data-testid="assembly-modal">
      <div className="bg-[#121214] border border-zinc-800 rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
          <div><h3 className="font-bold text-white">{detail.name}</h3><p className="text-xs text-zinc-500">{detail.assembly_no || ""}</p></div>
          <button onClick={onClose} className="text-zinc-400" data-testid="assembly-close-btn"><X size={20} /></button>
        </div>

        <div className={`flex-1 min-h-0 flex flex-col ${drawingPath ? "md:grid md:grid-cols-2" : ""}`}>
          {/* Drawing (left column) — only when a drawing exists */}
          {drawingPath && (
            <div className="p-4 border-b md:border-b-0 md:border-r border-zinc-800 overflow-auto max-h-[42vh] md:max-h-none">
              <>
                {hotspots.length === 0 && <p className="text-xs text-zinc-400 mb-2">{t("Pinch to zoom · tap a part number in the list")}</p>}
                <div className={`relative bg-white rounded-xl min-h-[240px] flex items-center justify-center ${zoom ? "overflow-auto" : "overflow-hidden"}`} data-testid="assembly-drawing">
                  <div className={`relative mx-auto ${zoom ? "w-[180%]" : "w-full"}`}>
                    <AuthImage path={drawingPath} alt="exploded drawing" className="w-full block" fit="contain" />
                    {hotspots.map((h, i) => (
                      <button key={i} data-testid={`hotspot-${h.pos}`} onClick={() => setSelPos(h.pos)}
                        style={{ left: `${h.x * 100}%`, top: `${h.y * 100}%` }}
                        className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-transform hover:scale-110 ${selPos === h.pos ? "bg-[#F26722] border-white text-white scale-110" : "bg-white/90 border-[#F26722] text-[#F26722]"}`}>
                        {h.pos}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={() => setZoom((z) => !z)} className="mt-2 text-xs text-zinc-400 flex items-center gap-1.5"><ZoomIn size={14} /> {zoom ? t("Reset zoom") : t("Zoom in")}</button>
              </>
            </div>
          )}

          {/* Parts list (scrolls) + selected part sticky footer */}
          <div className="flex flex-col min-h-0">
            {!drawingPath && (
              <div className="m-4 mb-0 bg-[#18181B] border border-zinc-800 rounded-xl p-3 text-sm text-zinc-300" data-testid="no-drawing-banner">
                {t("Tap a part number to see its details.")}
              </div>
            )}
            <div className="flex-1 overflow-auto p-4 space-y-1.5">
              {parts.length === 0 && <p className="text-zinc-500 text-sm text-center py-6">{t("No parts listed.")}</p>}
              {parts.map((p) => (
                <button key={p.id} data-testid={`part-row-${p.pos}`} onClick={() => setSelPos(p.pos)}
                  className={`w-full flex items-center gap-3 text-left rounded-lg px-3 py-2 border transition-colors ${selPos === p.pos ? "bg-[#3B1D0E]/40 border-[#F26722]" : "bg-[#141416] border-zinc-800 hover:border-zinc-700"}`}>
                  <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${selPos === p.pos ? "bg-[#F26722] text-white" : "bg-zinc-800 text-[#F26722]"}`}>{p.pos}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-white font-mono truncate">{p.part_number}</span>
                    <span className="block text-xs text-zinc-500 truncate">{p.description} ×{p.quantity ?? 1}</span>
                  </span>
                </button>
              ))}
            </div>
            {selected && (
              <div className="border-t border-zinc-800 bg-[#161618] p-3 shrink-0" data-testid="selected-part">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-[#F26722] font-bold">{t("Pos")} {selected.pos}</p>
                    <p className="font-bold text-white truncate">{selected.description || selected.part_number}</p>
                    <p className="text-xs text-zinc-400 font-mono truncate">{selected.part_number}{selected.legacy_no ? ` · ${t("was")} ${selected.legacy_no}` : ""} · ×{selected.quantity ?? 1}</p>
                  </div>
                  <button onClick={() => onAdd(selected)} data-testid="add-to-report-btn" className="shrink-0 bg-[#F26722] hover:bg-[#D95513] text-white font-bold text-sm px-4 py-2.5 rounded-lg flex items-center gap-1.5"><Plus size={16} /> {t("Add to report")}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
